import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Image as ImageIcon, Star, X, Pencil, Save, ArrowLeft, Loader2, Settings2 } from "lucide-react";
import {
  adminUsato, adminUsatoGet, createUsato, updateUsato, patchUsatoStatus, deleteUsato,
  usatoImages, addUsatoImage, delUsatoImage, reorderUsatoImages,
  adminUsatoGrades, putUsatoGrades,
  euro, USED_STATUSES, USED_STATUS_META, USED_PHOTO_CATEGORIES, mediaUrl,
} from "@/lib/store";

const fileToB64 = (file) => new Promise((res, rej) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
});

const EMPTY = { marca: "", modello: "", memoria: "", colore: "", prezzo_vendita_eur: "", costo_acquisto_eur: "",
  batteria_pct: "", grado: "", condizioni: "", test_effettuati: "", garanzia: "", stato: "IN_VERIFICA",
  promo_enabled: false, promo_price_eur: "", warranty_months: "12", warranty_active: true,
  imei: "", seriale: "", note_interne: "" };

const Input = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="mb-1 block text-xs text-zinc-400">{label}</span>
    <input data-testid={testid} {...p} className="w-full rounded-lg border border-white/10 bg-[#0C0F14] px-3 py-2 text-sm text-white outline-none focus:border-blue-500/50" />
  </label>
);
const Area = ({ label, testid, ...p }) => (
  <label className="block">
    <span className="mb-1 block text-xs text-zinc-400">{label}</span>
    <textarea data-testid={testid} rows={3} {...p} className="w-full rounded-lg border border-white/10 bg-[#0C0F14] px-3 py-2 text-sm text-white outline-none focus:border-blue-500/50" />
  </label>
);

function PhotoManager({ device, grades }) {
  const [imgs, setImgs] = useState([]);
  const [cat, setCat] = useState("frontale");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const load = () => usatoImages(device.id).then(setImgs).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [device.id]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const image_base64 = await fileToB64(f);
        await addUsatoImage(device.id, { image_base64, categoria: cat });
      }
      toast.success(`${files.length} foto caricate`); load();
    } catch { toast.error("Caricamento foto non riuscito"); } finally { setBusy(false); }
  };
  const setPrimary = async (iid) => {
    const order = [iid, ...imgs.filter((x) => x.id !== iid).map((x) => x.id)];
    await reorderUsatoImages(device.id, order); toast.success("Foto principale aggiornata"); load();
  };
  const del = async (iid) => { await delUsatoImage(device.id, iid); load(); };

  return (
    <div className="rounded-xl border border-white/10 bg-[#11141A] p-4" data-testid="usato-photos">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <ImageIcon className="h-4 w-4 text-zinc-400" />
        <span className="text-sm font-semibold text-white">Fotografie reali ({imgs.length})</span>
        <span className="text-xs text-zinc-500">· la prima è l'immagine principale</span>
      </div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select data-testid="usato-photo-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="rounded-lg border border-white/10 bg-[#0C0F14] px-2 py-1.5 text-xs text-white">
          {USED_PHOTO_CATEGORIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
        </select>
        <input ref={ref} type="file" accept="image/*" multiple onChange={onFiles} className="hidden" data-testid="usato-photo-input" />
        <button data-testid="usato-photo-add" disabled={busy} onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Carica foto
        </button>
      </div>
      {imgs.length === 0 ? (
        <p className="text-xs text-zinc-500">Nessuna foto. Carica le foto reali di questa unità.</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6" data-testid="usato-photo-grid">
          {imgs.map((im, i) => (
            <div key={im.id} className="group relative overflow-hidden rounded-lg border border-white/10">
              <img src={mediaUrl(im.url)} alt="" className="aspect-square w-full object-cover" />
              {i === 0 && <span className="absolute left-1 top-1 rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold text-white">PRINCIPALE</span>}
              <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 py-0.5 text-[9px] text-white">{(USED_PHOTO_CATEGORIES.find((c) => c.code === im.categoria) || {}).label || im.categoria}</span>
              <div className="absolute inset-x-0 top-1 flex justify-end gap-1 px-1 opacity-0 transition-opacity group-hover:opacity-100">
                {i !== 0 && <button data-testid={`usato-photo-primary-${im.id}`} onClick={() => setPrimary(im.id)} title="Imposta principale" className="rounded bg-white/90 p-1 text-amber-600"><Star className="h-3 w-3" /></button>}
                <button data-testid={`usato-photo-del-${im.id}`} onClick={() => del(im.id)} title="Elimina" className="rounded bg-white/90 p-1 text-rose-600"><X className="h-3 w-3" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GradesConfig({ onClose }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { adminUsatoGrades().then((g) => setRows(g.map((x) => ({ code: x.code, label: x.label, desc: x.desc })))).catch(() => {}); }, []);
  const save = async () => {
    try { await putUsatoGrades(rows.filter((r) => r.code)); toast.success("Gradi aggiornati"); onClose(); }
    catch { toast.error("Salvataggio gradi non riuscito"); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-[#11141A] p-4" data-testid="usato-grades-config">
      <div className="mb-3 flex items-center justify-between"><span className="text-sm font-semibold text-white">Configura gradi estetici</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="h-4 w-4" /></button></div>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <input value={r.code} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, code: e.target.value } : x))} placeholder="Codice" className="col-span-2 rounded border border-white/10 bg-[#0C0F14] px-2 py-1.5 text-xs text-white" data-testid={`usato-grade-code-${i}`} />
            <input value={r.label || ""} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Etichetta" className="col-span-4 rounded border border-white/10 bg-[#0C0F14] px-2 py-1.5 text-xs text-white" />
            <input value={r.desc || ""} onChange={(e) => setRows((p) => p.map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} placeholder="Descrizione" className="col-span-5 rounded border border-white/10 bg-[#0C0F14] px-2 py-1.5 text-xs text-white" />
            <button onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="col-span-1 text-rose-400"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={() => setRows((p) => [...p, { code: "", label: "", desc: "" }])} className="rounded-full border border-white/15 px-3 py-1.5 text-xs text-white">+ Grado</button>
        <button onClick={save} data-testid="usato-grades-save" className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white">Salva gradi</button>
      </div>
    </div>
  );
}

function Editor({ id, grades, onBack, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [device, setDevice] = useState(null);
  const [busy, setBusy] = useState(false);
  const isNew = id === "new";
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  useEffect(() => {
    if (isNew) { setF(EMPTY); setDevice(null); return; }
    adminUsatoGet(id).then((d) => {
      setDevice(d);
      setF({ marca: d.marca || "", modello: d.modello || "", memoria: d.memoria || "", colore: d.colore || "",
        prezzo_vendita_eur: d.prezzo_vendita_cents != null ? (d.prezzo_vendita_cents / 100).toString() : "",
        costo_acquisto_eur: d.costo_acquisto_cents != null ? (d.costo_acquisto_cents / 100).toString() : "",
        batteria_pct: d.batteria_pct ?? "", grado: d.grado || "", condizioni: d.condizioni || "",
        test_effettuati: d.test_effettuati || "", garanzia: d.garanzia || "", stato: d.stato || "IN_VERIFICA",
        promo_enabled: !!d.promo_enabled, promo_price_eur: d.promo_price_cents != null ? (d.promo_price_cents / 100).toString() : "",
        warranty_months: (d.warranty_months ?? 12).toString(), warranty_active: d.warranty_active !== false,
        imei: d.imei || "", seriale: d.seriale || "", note_interne: d.note_interne || "" });
    }).catch(() => toast.error("Dispositivo non trovato"));
  }, [id, isNew]);

  const payload = () => ({
    marca: f.marca.trim(), modello: f.modello.trim(), memoria: f.memoria || null, colore: f.colore || null,
    prezzo_vendita_cents: Math.round(parseFloat(f.prezzo_vendita_eur || "0") * 100) || 0,
    costo_acquisto_cents: f.costo_acquisto_eur === "" ? null : Math.round(parseFloat(f.costo_acquisto_eur) * 100),
    batteria_pct: f.batteria_pct === "" ? null : parseInt(f.batteria_pct, 10),
    grado: f.grado || null, condizioni: f.condizioni || null, test_effettuati: f.test_effettuati || null,
    garanzia: f.garanzia || null, stato: f.stato,
    promo_enabled: !!f.promo_enabled,
    promo_price_cents: f.promo_enabled && f.promo_price_eur !== "" ? Math.round(parseFloat(f.promo_price_eur) * 100) : null,
    warranty_months: f.warranty_months === "" ? 12 : parseInt(f.warranty_months, 10),
    warranty_active: !!f.warranty_active,
    imei: f.imei || null, seriale: f.seriale || null, note_interne: f.note_interne || null,
  });

  const save = async () => {
    if (!f.marca.trim() || !f.modello.trim()) { toast.error("Marca e modello obbligatori"); return; }
    setBusy(true);
    try {
      if (isNew) { const d = await createUsato(payload()); setDevice(d); toast.success("Dispositivo creato — ora carica le foto"); onSaved?.(); }
      else { const d = await updateUsato(id, payload()); setDevice(d); toast.success("Modifiche salvate"); onSaved?.(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); } finally { setBusy(false); }
  };

  const margine = (() => {
    const p = parseFloat(f.prezzo_vendita_eur || "0"), c = parseFloat(f.costo_acquisto_eur || "");
    return (!isNaN(p) && !isNaN(c)) ? (p - c) : null;
  })();

  return (
    <div className="space-y-4" data-testid="usato-editor">
      <button onClick={onBack} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white"><ArrowLeft className="h-4 w-4" /> Torna all'elenco</button>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-[#11141A] p-4">
            <h3 className="mb-3 text-sm font-semibold text-white">Dati dispositivo</h3>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Marca *" testid="usato-f-marca" value={f.marca} onChange={set("marca")} />
              <Input label="Modello *" testid="usato-f-modello" value={f.modello} onChange={set("modello")} />
              <Input label="Memoria" testid="usato-f-memoria" value={f.memoria} onChange={set("memoria")} placeholder="128GB" />
              <Input label="Colore" testid="usato-f-colore" value={f.colore} onChange={set("colore")} />
              <Input label="Prezzo vendita (€)" testid="usato-f-prezzo" type="number" value={f.prezzo_vendita_eur} onChange={set("prezzo_vendita_eur")} />
              <Input label="Batteria %" testid="usato-f-batteria" type="number" value={f.batteria_pct} onChange={set("batteria_pct")} />
              <label className="block"><span className="mb-1 block text-xs text-zinc-400">Grado estetico</span>
                <select data-testid="usato-f-grado" value={f.grado} onChange={set("grado")} className="w-full rounded-lg border border-white/10 bg-[#0C0F14] px-3 py-2 text-sm text-white">
                  <option value="">—</option>{grades.map((g) => <option key={g.code} value={g.code}>{g.label || g.code}</option>)}
                </select></label>
              <Input label="Garanzia" testid="usato-f-garanzia" value={f.garanzia} onChange={set("garanzia")} placeholder="12 mesi" />
            </div>
            <div className="mt-3 space-y-3">
              <Area label="Condizioni estetiche (difetti)" testid="usato-f-condizioni" value={f.condizioni} onChange={set("condizioni")} placeholder="Display: micrograffi&#10;Cornice: piccoli segni&#10;Retro: ottimo" />
              <Area label="Test effettuati" testid="usato-f-test" value={f.test_effettuati} onChange={set("test_effettuati")} />
            </div>
          </div>

          <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
            <h3 className="mb-1 text-sm font-semibold text-amber-300">🔒 Dati privati (solo interni — mai pubblici)</h3>
            <p className="mb-3 text-xs text-amber-200/70">IMEI, seriale, costo e note non sono mai esposti su sito/AI.</p>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Costo acquisto (€)" testid="usato-f-costo" type="number" value={f.costo_acquisto_eur} onChange={set("costo_acquisto_eur")} />
              <div className="flex items-end"><div className="text-xs text-zinc-400">Margine stimato: <span className="font-semibold text-white">{margine != null ? `${margine.toFixed(2)} €` : "—"}</span></div></div>
              <Input label="IMEI" testid="usato-f-imei" value={f.imei} onChange={set("imei")} />
              <Input label="Seriale" testid="usato-f-seriale" value={f.seriale} onChange={set("seriale")} />
            </div>
            <div className="mt-3"><Area label="Note interne" testid="usato-f-note" value={f.note_interne} onChange={set("note_interne")} /></div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-white/10 bg-[#11141A] p-4">
            <h3 className="mb-3 text-sm font-semibold text-white">Stato & salvataggio</h3>
            <label className="block"><span className="mb-1 block text-xs text-zinc-400">Stato</span>
              <select data-testid="usato-f-stato" value={f.stato} onChange={set("stato")} className="w-full rounded-lg border border-white/10 bg-[#0C0F14] px-3 py-2 text-sm text-white">
                {USED_STATUSES.map((s) => <option key={s} value={s}>{USED_STATUS_META[s]?.label || s}</option>)}
              </select></label>
            <p className="mt-2 text-xs text-zinc-500">Pubblico solo DISPONIBILE. VENDUTO/RISERVATO/IN VERIFICA non compaiono sul sito nè nella vetrina homepage.</p>
            <div className="mt-4 space-y-3 rounded-lg border border-white/10 bg-[#0C0F14] p-3">
              <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" data-testid="usato-f-promo-on" checked={f.promo_enabled} onChange={(e) => setF((p) => ({ ...p, promo_enabled: e.target.checked }))} /> Promo attiva su questa unità</label>
              {f.promo_enabled && <Input label="Prezzo promo (€) — ≤ prezzo normale" testid="usato-f-promo-price" type="number" value={f.promo_price_eur} onChange={set("promo_price_eur")} />}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Input label="Garanzia (mesi)" testid="usato-f-warranty-months" type="number" value={f.warranty_months} onChange={set("warranty_months")} />
              <label className="flex items-end gap-2 pb-2 text-sm text-white"><input type="checkbox" data-testid="usato-f-warranty-active" checked={f.warranty_active} onChange={(e) => setF((p) => ({ ...p, warranty_active: e.target.checked }))} /> Garanzia attiva</label>
            </div>
            <button data-testid="usato-save" disabled={busy} onClick={save} className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {isNew ? "Crea dispositivo" : "Salva modifiche"}
            </button>
          </div>
          {device ? <PhotoManager device={device} grades={grades} /> : (
            <div className="rounded-xl border border-dashed border-white/10 bg-[#11141A] p-4 text-xs text-zinc-500">Salva prima il dispositivo per caricare le fotografie reali.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Usato() {
  const [list, setList] = useState(null);
  const [grades, setGrades] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null); // id | "new" | null
  const [showGrades, setShowGrades] = useState(false);

  const load = () => adminUsato(filter ? { stato: filter } : {}).then(setList).catch(() => setList([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { adminUsatoGrades().then(setGrades).catch(() => {}); }, []);

  const changeStatus = async (id, stato) => { await patchUsatoStatus(id, stato); toast.success("Stato aggiornato"); load(); };
  const remove = async (id) => { if (!window.confirm("Eliminare questo dispositivo?")) return; await deleteUsato(id); toast.success("Eliminato"); load(); };

  if (editing) return <Editor id={editing} grades={grades} onBack={() => { setEditing(null); load(); }} onSaved={() => adminUsatoGrades().then(setGrades).catch(() => {})} />;

  return (
    <div className="space-y-5" data-testid="admin-usato">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Usato IPHIX</h2>
          <p className="text-xs text-zinc-500">Catalogo dispositivi usati/ricondizionati — unità singole con foto reali.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="usato-grades-toggle" onClick={() => setShowGrades((v) => !v)} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/5"><Settings2 className="h-3.5 w-3.5" /> Gradi</button>
          <button data-testid="usato-add-btn" onClick={() => setEditing("new")} className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500"><Plus className="h-4 w-4" /> Aggiungi dispositivo</button>
        </div>
      </div>

      {showGrades && <GradesConfig onClose={() => { setShowGrades(false); adminUsatoGrades().then(setGrades).catch(() => {}); }} />}

      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`rounded-full px-3 py-1 text-xs ${filter === "" ? "bg-white/15 text-white" : "text-zinc-400 hover:text-white"}`}>Tutti</button>
        {USED_STATUSES.map((s) => (
          <button key={s} data-testid={`usato-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1 text-xs ${filter === s ? "bg-white/15 text-white" : "text-zinc-400 hover:text-white"}`}>{USED_STATUS_META[s]?.label || s}</button>
        ))}
      </div>

      {list === null ? <p className="text-zinc-500">Caricamento…</p> : list.length === 0 ? (
        <div data-testid="usato-admin-empty" className="rounded-xl border border-dashed border-white/10 p-10 text-center text-zinc-500">Nessun dispositivo. Clicca "Aggiungi dispositivo".</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-left text-xs uppercase tracking-wide text-zinc-500">
              <tr><th className="p-3">Foto</th><th className="p-3">Dispositivo</th><th className="p-3">Prezzo</th><th className="p-3">Margine</th><th className="p-3">Grado</th><th className="p-3">Stato</th><th className="p-3 text-right">Azioni</th></tr>
            </thead>
            <tbody>
              {list.map((d) => (
                <tr key={d.id} data-testid={`usato-row-${d.id}`} className="border-t border-white/5">
                  <td className="p-3">{d.image ? <img src={mediaUrl(d.image)} alt="" className="h-12 w-12 rounded object-cover" /> : <div className="flex h-12 w-12 items-center justify-center rounded bg-white/5 text-zinc-600"><ImageIcon className="h-4 w-4" /></div>}</td>
                  <td className="p-3"><div className="font-medium text-white">{d.marca} {d.modello}</div><div className="text-xs text-zinc-500">{[d.memoria, d.colore].filter(Boolean).join(" · ")}</div></td>
                  <td className="p-3 text-white">{euro(d.prezzo_vendita_cents)}</td>
                  <td className="p-3 text-zinc-400">{d.margine_cents != null ? euro(d.margine_cents) : "—"}</td>
                  <td className="p-3 text-zinc-300">{d.grado || "—"}</td>
                  <td className="p-3">
                    <select data-testid={`usato-status-${d.id}`} value={d.stato} onChange={(e) => changeStatus(d.id, e.target.value)} className={`rounded-full border px-2 py-1 text-xs ${USED_STATUS_META[d.stato]?.cls || "text-white"}`}>
                      {USED_STATUSES.map((s) => <option key={s} value={s} className="bg-[#11141A] text-white">{USED_STATUS_META[s]?.label || s}</option>)}
                    </select>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button data-testid={`usato-edit-${d.id}`} onClick={() => setEditing(d.id)} className="rounded-lg border border-white/10 p-1.5 text-zinc-300 hover:text-white"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`usato-del-${d.id}`} onClick={() => remove(d.id)} className="rounded-lg border border-white/10 p-1.5 text-rose-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
